import { parseCallAnsweredPayload } from '../../infrastructure/adapters/voiceSocketEventAdapter';
import { voiceRemoveIncomingCall, voiceSetActiveCall } from '../../../../../actions/voice/voiceActions';
import {
  clearAgentAnswerBridgeOwnership,
  markAgentCallAnswerPending
} from '../../infrastructure/webrtc/voiceAgentWebRtcAdapter';
import getOrCreateVoiceTabSessionId from '../../infrastructure/webrtc/voiceTabSession';
import {
  getVoiceTabClaimedCallControlId,
  isVoiceTabAnswerClaimed,
  markVoiceCallAnswered,
  releaseVoiceTabAnswer
} from '../voiceHangupCoordinator';
import { isCallAnsweredOwnedByThisTab } from '../../domain/voiceTabCallOwnership';
import { voiceSocketLog } from '../../../../utils/voiceSocketDebug';
import { notifyVoiceIncomingCallAnswered } from '../voiceIncomingCallAnsweredNotifier';
import {
  ensureOutboundWebRtcMediaConnected,
  isOutgoingCallDirection,
  kickAgentRemoteAudioAfterCallAnswered,
  mergeActiveCallFromAnswered
} from './voiceSocketCallUtils';

export default function callAnsweredHandler(payload, ports) {
  const parsed = parseCallAnsweredPayload(payload);
  if (!parsed) {
    voiceSocketLog('call_answered ignored (invalid payload)', payload);
    return;
  }

  const {
    incomingCalls,
    activeCall,
    outboundDialingCall
  } = ports.getHandlerState();
  const matchedIncoming = incomingCalls.find(
    (call) => call.callControlId === parsed.callControlId
  );
  const matchesActiveCall = activeCall?.callControlId === parsed.callControlId;
  const matchesOutboundDialing = outboundDialingCall?.callControlId === parsed.callControlId;
  const localTabSessionId = getOrCreateVoiceTabSessionId();
  const ownedByThisTab = isCallAnsweredOwnedByThisTab({
    payloadTabSessionId: parsed.tabSessionId,
    localTabSessionId,
    callControlId: parsed.callControlId,
    activeCallControlId: activeCall?.callControlId ?? null,
    outboundDialingCallControlId: outboundDialingCall?.callControlId ?? null,
    thisTabClaimedCallControlId: getVoiceTabClaimedCallControlId()
  });
  const isOutboundAnswered = isOutgoingCallDirection(parsed.direction)
    || isOutgoingCallDirection(activeCall?.direction)
    || isOutgoingCallDirection(outboundDialingCall?.direction)
    || (matchesOutboundDialing && !matchedIncoming);

  voiceSocketLog('dispatch call_answered', {
    callControlId: parsed.callControlId,
    tabSessionId: parsed.tabSessionId,
    localTabSessionId,
    ownedByThisTab,
    matchedIncoming: Boolean(matchedIncoming),
    hasActiveCall: Boolean(activeCall),
    matchesOutboundDialing,
    isOutboundAnswered
  });

  ports.dispatch(voiceRemoveIncomingCall(parsed.callControlId));

  if (!ownedByThisTab) {
    if (isVoiceTabAnswerClaimed(parsed.callControlId)) {
      releaseVoiceTabAnswer(parsed.callControlId);
      markAgentCallAnswerPending(null);
      clearAgentAnswerBridgeOwnership();
    }
    voiceSocketLog('call_answered dropped from queue (other tab/agent)', {
      callControlId: parsed.callControlId,
      tabSessionId: parsed.tabSessionId
    });
    return;
  }

  markVoiceCallAnswered(parsed.callControlId);

  if (activeCall?.callControlId === parsed.callControlId) {
    ports.dispatch(voiceSetActiveCall(mergeActiveCallFromAnswered(activeCall, parsed)));
    kickAgentRemoteAudioAfterCallAnswered();
    notifyVoiceIncomingCallAnswered();
    return;
  }

  if (matchedIncoming) {
    ports.dispatch(voiceSetActiveCall(mergeActiveCallFromAnswered(matchedIncoming, parsed)));
    kickAgentRemoteAudioAfterCallAnswered();
    notifyVoiceIncomingCallAnswered();
    return;
  }

  if (isOutboundAnswered && (
    matchesActiveCall
    || matchesOutboundDialing
    || isVoiceTabAnswerClaimed(parsed.callControlId)
  )) {
    const baseCall = activeCall ?? outboundDialingCall;
    ports.dispatch(voiceSetActiveCall(
      mergeActiveCallFromAnswered(baseCall, parsed)
    ));
    notifyVoiceIncomingCallAnswered();
    ensureOutboundWebRtcMediaConnected(parsed.callControlId).catch((error) => {
      voiceSocketLog('outbound WebRTC media connect failed on call_answered', {
        message: error instanceof Error ? error.message : String(error)
      });
    });
    return;
  }

  voiceSocketLog('call_answered ignored (no local dial/claim/active context)', {
    callControlId: parsed.callControlId,
    tabSessionId: parsed.tabSessionId
  });
}
